import { useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useToast } from '@cad-arc';
import { useWorkspaceWindowType } from '../common/hooks/use-workspace-window-type';
import { formatSensorPlace, formatSensorPriority, getSensorAlertTags } from './helpers';
import { useOpenSensorPanelsOnMap } from './sensor-panel-map-actions';
import { useSensorsEnabled } from './use-sensors-enabled';
import { useSensorsStore } from './use-sensors-store';

/**
 * Raises a toast per genuinely new sensor alert. Its button centres the map on the camera and
 * opens the camera's panel; the map itself never moves when an alert arrives.
 *
 * This no longer subscribes to the stream. Link and unlink now push the whole updated alert in the
 * same shape as a new one, so the only thing separating "new" from "already had it, just linked" is
 * whether this client already held the key — and that is only answerable at the moment of ingest.
 * `useIntegratedSensors` is the single stream subscriber, decides it there, and queues the new ones;
 * this drains that queue. One writer, so the answer cannot depend on subscriber ordering.
 *
 * It also means replayed history is silent for free: a cold-start or catch-up fetch ingests as
 * not-live and queues nothing.
 */
export const SensorAlertListener = observer(function SensorAlertListener(): null {
    const sensorsEnabled = useSensorsEnabled();
    const workspaceWindowType = useWorkspaceWindowType();
    const toast = useToast();
    const { openSensorPanelOnMap } = useOpenSensorPanelsOnMap();
    const sensorsStore = useSensorsStore();

    // Only while the map is showing: the Electron map window, or the `/map` route on web.
    const canToast = sensorsEnabled && workspaceWindowType === 'MAP_V2';
    const alertsAwaitingToast = sensorsStore.alertsAwaitingToast;

    useEffect(
        function toastNewSensorAlerts() {
            if (alertsAwaitingToast.length === 0) {
                return;
            }
            /*
             * Drained even when this window cannot toast. Otherwise the queue would grow for the
             * life of a non-map Electron window, and the map window's own store — a separate
             * renderer with its own instance — is what actually raises the toast.
             */
            if (canToast) {
                alertsAwaitingToast.forEach((alert) => {
                    /*
                     * Priority, then where it happened, then what happened: site and street, then
                     * the alert's own classifications. The alert carries only `sensorExternalId`,
                     * so the place is joined from the sensor directory we already hold. A camera
                     * absent from it — not yet loaded, or deactivated since — leaves the place
                     * unknown rather than suppressing the toast.
                     *
                     * The address is PII under CJIS. Fine on screen, never in a log line.
                     */
                    const sensor = sensorsStore.getSensorForAlert(alert);
                    const place = formatSensorPlace(sensor) ?? sensor?.name ?? 'Unknown sensor';
                    const classifications = getSensorAlertTags(alert).join(' · ');

                    toast({
                        status: 'default',
                        // Priority first when the alert type has one mapped: it is what decides
                        // whether the toast is worth acting on.
                        description: [formatSensorPriority(alert.sensorPriority), place, classifications]
                            .filter(Boolean)
                            .join(' · '),
                        // Arc's toast takes one action, so this one both centres and opens.
                        action: {
                            children: 'View Sensor',
                            onClick() {
                                openSensorPanelOnMap(alert.sensorExternalId, { centerMap: true });
                            },
                        },
                    });
                });
            }
            sensorsStore.clearAlertsAwaitingToast();
        },
        [alertsAwaitingToast, canToast, openSensorPanelOnMap, sensorsStore, toast]
    );

    return null;
});
